/**
 * プロフィール入力のサブステップ進捗バー（垂直タイムライン形式）
 *
 * 6ステップすべてを表示:
 * - 完了: チェックアイコン（緑）
 * - 現在: 実心の丸（ハイライト）
 * - 未完了: 空心の丸
 */
import I18n from "common/i18n";
import { ProfileSubStep, FavoritesSubStep, ALL_PROFILE_STEPS } from "../types";

// カラー定数
const COLORS = {
  completed: "#70b1f4", // 青（btn-blueと同じ）
  current: "#3f4a61", // 現在ステップ
  pending: "#c2c9cf", // 未完了
  line: "#e0e4e7", // 接続線
};

// サイズ定数
const DOT_SIZE = 32;
const LINE_WIDTH = 2;
const LINE_HEIGHT = 32;

interface ProfileSubStepBarProps {
  currentSubStep: ProfileSubStep;
  favoritesSubStep?: FavoritesSubStep;
  onStepClick?: (profileSubStep: ProfileSubStep, favoritesSubStep?: FavoritesSubStep) => void;
  /**
   * 入力済みステップのIDリスト
   * 指定された場合、現在のステップより前かどうかではなく、
   * このリストに含まれるかどうかで完了状態を判定する
   */
  completedStepIds?: string[];
}

const getCurrentStepIndex = (
  currentSubStep: ProfileSubStep,
  favoritesSubStep?: FavoritesSubStep,
): number => {
  return ALL_PROFILE_STEPS.findIndex((step) => {
    if (step.profileSubStep !== currentSubStep) return false;
    if (step.favoritesSubStep) {
      return step.favoritesSubStep === favoritesSubStep;
    }
    return true;
  });
};

// アイコンステップのID
const ICON_STEP_ID = "icon";
// アイコンステップ以外の全ステップID
const NON_ICON_STEP_IDS = ALL_PROFILE_STEPS.filter((s) => s.id !== ICON_STEP_ID).map((s) => s.id);

export const ProfileSubStepBar = ({
  currentSubStep,
  favoritesSubStep,
  onStepClick,
  completedStepIds,
}: ProfileSubStepBarProps) => {
  const currentIndex = getCurrentStepIndex(currentSubStep, favoritesSubStep);
  const steps = ALL_PROFILE_STEPS;

  // すべての非アイコンステップが完了しているかどうか
  const allNonIconStepsCompleted = completedStepIds
    ? NON_ICON_STEP_IDS.every((id) => completedStepIds.includes(id))
    : false;

  // ステップがクリック可能かどうかを判定
  const isStepClickable = (index: number, stepId: string): boolean => {
    if (!onStepClick) return false;
    if (index === currentIndex) return false;

    if (completedStepIds) {
      // completedStepIdsモード（IconSelectorフロー）
      if (stepId === ICON_STEP_ID) {
        // アイコンステップ: 他の全ステップが完了済みの場合のみクリック可能
        return allNonIconStepsCompleted;
      }
      // 非アイコンステップ: 常にクリック可能
      return true;
    }

    // 位置ベースモード（CreateUserWizardフロー）: 現在より前のステップのみ
    return index < currentIndex;
  };

  const handleStepClick = (index: number, stepId: string) => {
    if (isStepClickable(index, stepId)) {
      const step = steps[index];
      onStepClick!(step.profileSubStep, step.favoritesSubStep);
    }
  };

  return (
    <div className="flex-col-el py-6 px-6 bg-white border-4 border-[#c2c9cf] rounded-3xl">
      {steps.map((step, index) => {
        const isCurrent = index === currentIndex;
        // 現在のステップの場合は完了扱いにしない（チェックマークと白い点が同時に表示されるのを防ぐ）
        const isCompleted = isCurrent
          ? false
          : completedStepIds
            ? completedStepIds.includes(step.id)
            : index < currentIndex;
        const isLast = index === steps.length - 1;
        const clickable = isStepClickable(index, step.id);

        return (
          <div key={step.id} className="flex-row-el">
            {/* 左側：アイコンと接続線 */}
            <div className="flex-col-el items-center w-8">
              {/* ドット/アイコン */}
              <div
                className={`flex-col-el flex-center rounded-full w-8 h-8 ${
                  isCompleted ? "bg-[#70b1f4]" : "bg-transparent border-2 border-[#c2c9cf]"
                } ${isCurrent ? "shadow-[0_4px_16px_rgba(112,177,244,0.6)]" : ""} ${
                  clickable ? "cursor-pointer" : ""
                }`}
                onClick={() => handleStepClick(index, step.id)}
              >
                {isCompleted && (
                  <span className="material-symbols-outlined text-[20px] text-white">check</span>
                )}
              </div>
              {/* 接続線 */}
              {!isLast && (
                <div className={`w-0.5 h-8 ${isCompleted ? "bg-[#70b1f4]" : "bg-[#e0e4e7]"}`} />
              )}
            </div>

            {/* 右側：ラベル */}
            <p
              className={`text-body text-[24px] whitespace-nowrap !ml-3 leading-8 ${
                isCompleted || isCurrent ? "text-[#70b1f4]" : "text-[#3f4a61]"
              } ${clickable ? "cursor-pointer" : ""}`}
              onClick={() => handleStepClick(index, step.id)}
            >
              {I18n.t(step.labelKey)}
            </p>
          </div>
        );
      })}
    </div>
  );
};
